'use client'

import { useCallback, useEffect, useRef, useState, type ReactNode } from 'react'
import Link from 'next/link'
import { useSearchParams } from 'next/navigation'
import { useTranslations } from 'next-intl'
import { ArrowLeft, Loader2 } from 'lucide-react'
import { toast } from 'sonner'

import { Alert } from '@/components/ui/alert'
import { Button } from '@/components/ui/button'
import { Field } from '@/components/encuentro/ui/field'
import { formatWaitTime } from '@/lib/utils/format-wait'

import { AuthHeading, AuthShell, authLinkClass } from './auth-shell'
import type { FormAuthAction } from './types'

const COOLDOWN_SECONDS = 10

/**
 * Ask for a password reset link (ALU-73). Same flow as the classic
 * `ForgotPasswordForm`: `forgotPassword` unchanged, the cooldown it asks for
 * (`retryAfter`, 10 s by default) after an error, `?email=` prefilled and
 * `?error=` announced.
 */
export function ForgotPasswordScreen({
  forgotPassword,
  locale,
}: {
  forgotPassword: FormAuthAction
  locale?: ReactNode
}) {
  const t = useTranslations('encuentro.auth')
  const [isLoading, setIsLoading] = useState(false)
  const [isSubmitted, setIsSubmitted] = useState(false)
  const [cooldown, setCooldown] = useState(0)
  const cooldownRef = useRef<NodeJS.Timeout | null>(null)
  const searchParams = useSearchParams()

  const startCooldown = useCallback((seconds: number) => {
    setCooldown(seconds)
    if (cooldownRef.current) clearInterval(cooldownRef.current)
    cooldownRef.current = setInterval(() => {
      setCooldown((prev) => {
        if (prev <= 1) {
          if (cooldownRef.current) clearInterval(cooldownRef.current)
          return 0
        }
        return prev - 1
      })
    }, 1000)
  }, [])

  useEffect(() => {
    return () => {
      if (cooldownRef.current) clearInterval(cooldownRef.current)
    }
  }, [])

  useEffect(() => {
    // The fixed title only: the raw `?error=` text is never echoed (ALU-518).
    if (searchParams.get('error')) toast.error(t('forgot.toast.linkFailed'))
  }, [searchParams, t])

  const handleSubmit = async (formData: FormData) => {
    if (cooldown > 0) return
    setIsLoading(true)
    try {
      const result = await forgotPassword(formData)
      if (result?.error) {
        startCooldown(typeof result.retryAfter === 'number' ? result.retryAfter : COOLDOWN_SECONDS)
        toast.error(t('forgot.toast.failed'), { description: result.error })
      } else {
        setIsSubmitted(true)
        toast.success(t('forgot.toast.sent'), { description: t('forgot.toast.sentBody') })
      }
    } catch {
      toast.error(t('forgot.toast.somethingWrong'))
    } finally {
      setIsLoading(false)
    }
  }

  const backToLogin = (
    <Link href="/login" className={`inline-flex items-center gap-2 text-en-meta ${authLinkClass}`}>
      <ArrowLeft aria-hidden className="size-4" />
      {t('forgot.backToLogin')}
    </Link>
  )

  return (
    <AuthShell org={null} locale={locale}>
      <div data-testid="encuentro-forgot-password" className="grid gap-6">
        <AuthHeading title={t('forgot.title')} subtitle={t('forgot.subtitle')} />
        {isSubmitted ? (
          <div className="grid gap-6">
            <Alert
              look="encuentro"
              tone="positive"
              title={t('forgot.sentTitle')}
              body={t('forgot.sentBody')}
            />
            <Button look="encuentro" variant="secondary" block asChild>
              <Link href="/login">{t('forgot.backToLogin')}</Link>
            </Button>
          </div>
        ) : (
          <form className="grid gap-5" action={handleSubmit}>
            <Field
              label={t('fields.email')}
              id="email"
              name="email"
              type="email"
              autoComplete="email"
              required
              placeholder={t('fields.emailPlaceholder')}
              inputMode="email"
              enterKeyHint="done"
              disabled={isLoading}
              defaultValue={searchParams.get('email') ?? ''}
            />
            <Button
              look="encuentro"
              variant="primary"
              type="submit"
              block
              disabled={isLoading || cooldown > 0}
            >
              {isLoading && <Loader2 aria-hidden className="animate-spin" />}
              {isLoading
                ? t('forgot.sending')
                : cooldown > 0
                  ? t('forgot.wait', { wait: formatWaitTime(cooldown, t) })
                  : t('forgot.submit')}
            </Button>
            <div className="flex justify-center">{backToLogin}</div>
          </form>
        )}
      </div>
    </AuthShell>
  )
}
